import { fg } from '@atlassian/jira-feature-gating';
import {
	DEFAULT_CURRENCY_CODE,
	DEFAULT_CURRENCY_DISPLAY,
	FORMAT_STYLE_CURRENCY,
	FORMAT_STYLE_DECIMAL,
	FORMAT_STYLE_PERCENT,
	FORMAT_STYLE_UNIT,
	FORMAT_UNIT_PERCENT,
	type CurrencyDisplayOptions,
	type FormatStyle,
	type NumberFieldDisplayConfig,
	type NumberFieldDisplayOptions,
	type AggJiraNumberFieldFormatConfigType,
} from './types.tsx';
import type { FormatConfigurationType } from './ui/number-field-configuration/types.tsx';

/**
 * This is due to Jira commonly using the POSIX format (using '_' for defining locales),
 * such as in jira/src/packages/platform/supported-locales/src/supported-locales.tsx,
 * whereas the Intl Javascript API requires locales to be in the IETF BCP 47 format.
 */
const coerceBCPLocaleFormat = (locale: string): string => {
	return locale.replace('_', '-');
};

// Converts strings to lower case while also preventing string literal types from being converted
// into the more general `string` type as the String.prototype.toLowerCase() method always returns
// a `string` type, even if the string is a string literal. For example:
// const string = 'CONSTANT'; // type is `'CONSTANT'`;
// string.toLowerCase() // returned type is `string`;
// toLowerCase(string) // returned type is `'constant'`;
const toLowerCase = <T extends string>(inputString: T): Lowercase<T> => {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	return inputString.toLocaleLowerCase() as Lowercase<T>;
};

const isSupportedCurrencyCode = (currencyCode: string): boolean => {
	// Check if the currency code is supported
	return Intl.supportedValuesOf(toLowerCase(FORMAT_STYLE_CURRENCY)).includes(currencyCode);
};

const isSupportedUnit = (unit: string): boolean => {
	// Check if the unit is supported
	return Intl.supportedValuesOf(toLowerCase(FORMAT_STYLE_UNIT)).includes(unit);
};

export const formatCurrency = (amount: number, config: CurrencyDisplayOptions = {}): string => {
	const {
		currencyCode = DEFAULT_CURRENCY_CODE,
		locale,
		currencyDisplay = DEFAULT_CURRENCY_DISPLAY,
		...rest
	} = config;

	// Check if the currency code is supported. fall back to decimal format if not.
	// @ts-expect-error - TS2322 Typescript 5.9.2 upgrade
	const formatOptions: Intl.NumberFormatOptions = isSupportedCurrencyCode(currencyCode)
		? {
				style: FORMAT_STYLE_CURRENCY.toLocaleLowerCase(),
				currency: currencyCode,
				currencyDisplay,
				maximumFractionDigits: Number.isInteger(amount) ? 0 : undefined,
				...rest,
			}
		: {
				style: FORMAT_STYLE_DECIMAL.toLocaleLowerCase(),
				...rest,
			};

	const formatter = new Intl.NumberFormat(locale && coerceBCPLocaleFormat(locale), formatOptions);
	return formatter.format(amount ?? 0);
};

export const formatPercent = (amount: number, config: NumberFieldDisplayOptions = {}): string => {
	const { locale, ...rest } = config;
	return new Intl.NumberFormat(locale && coerceBCPLocaleFormat(locale), {
		...rest,
		// @ts-expect-error - TS2769 Typescript 5.9.2 upgrade
		style: FORMAT_STYLE_UNIT.toLocaleLowerCase(),
		unit: FORMAT_UNIT_PERCENT,
	}).format(amount ?? 0);
};

export const formatUnits = (number: number, config: NumberFieldDisplayOptions = {}): string => {
	const { unit, locale, ...rest } = config;

	// Check if the unit code is supported. fall back to decimal format if not.
	// @ts-expect-error - TS2322 Typescript 5.9.2 upgrade
	const formatOptions: Intl.NumberFormatOptions =
		unit && isSupportedUnit(unit)
			? {
					style: FORMAT_STYLE_UNIT.toLocaleLowerCase(),
					unit,
					...rest,
				}
			: {
					style: FORMAT_STYLE_DECIMAL.toLocaleLowerCase(),
					...rest,
				};
	const formatter = new Intl.NumberFormat(locale && coerceBCPLocaleFormat(locale), formatOptions);
	return formatter.format(number ?? 0);
};

export const formatDecimal = (number: number, config: NumberFieldDisplayOptions = {}): string => {
	const formatter = new Intl.NumberFormat(config.locale && coerceBCPLocaleFormat(config.locale), {
		// @ts-expect-error - TS2769 Typescript 5.9.2 upgrade
		style: FORMAT_STYLE_DECIMAL.toLocaleLowerCase(),
		...config,
	});
	return formatter.format(number ?? 0);
};

export const formatNumberField = (
	number: number,
	config: NumberFieldDisplayConfig = { formatStyle: FORMAT_STYLE_DECIMAL },
): string => {
	const { formatStyle, formatUnit, locale } = config;
	switch (formatStyle) {
		case FORMAT_STYLE_CURRENCY:
			return formatUnit
				? formatCurrency(number, { locale, currencyCode: formatUnit })
				: formatDecimal(number, { locale });
		case FORMAT_STYLE_PERCENT:
			return formatPercent(number, { locale });
		case FORMAT_STYLE_UNIT:
			return formatUnit
				? formatUnits(number, { locale, unit: formatUnit })
				: formatDecimal(number, { locale });
		default: // fall back to decimal, we will add more formats in the future
			return formatDecimal(number);
	}
};

// This function is used to transform a formatted number to the UI format.
// For example, if the number is 1 and the formatStyle is FORMAT_STYLE_PERCENT,
// the function will return 100. And UI will display 100%.
// for other formats, the function will return the number as is.
// this function should be used right after a custom number field value return from backend
export const transformToUI = (
	number?: number | null,
	formatStyle: string | null = FORMAT_STYLE_DECIMAL,
) =>
	number &&
	formatStyle === FORMAT_STYLE_PERCENT &&
	fg('frontier-currency-field-fix-percentage-storage')
		? number * 100
		: number;

// This function is used to transform a formatted number to the server format.
// For example, if the number is 100 and the formatStyle is FORMAT_STYLE_PERCENT,
// the function will return 1. And server will receive 1.
// for other formats, the function will return the number as is.
// this function should be used right before a custom number field value is sent to backend
export const transformToServer = (
	number?: number | null,
	formatStyle: string | null = FORMAT_STYLE_DECIMAL,
) => {
	if (
		!number ||
		formatStyle !== FORMAT_STYLE_PERCENT ||
		!fg('frontier-currency-field-fix-percentage-storage')
	) {
		return number;
	}
	// Use Number.EPSILON to avoid floating-point precision issues
	const result = number / 100;
	const factor = Math.pow(10, 8); // 8 decimal places should be sufficient for percentage precision
	return Math.round((result + Number.EPSILON) * factor) / factor;
};

export const isSidebarFormatConfigEnabled = () =>
	// eslint-disable-next-line jira/ff/no-preconditioning
	fg('frontier-currency-field-d1') && fg('fields-settings-currency-field-display');

const formatStyleMapper = (
	jiraFormatStyle: AggJiraNumberFieldFormatConfigType['formatStyle'],
): FormatStyle => {
	switch (jiraFormatStyle) {
		case 'CURRENCY':
			return FORMAT_STYLE_CURRENCY;
		case 'PERCENT':
			return FORMAT_STYLE_PERCENT;
		case 'UNIT':
			return FORMAT_STYLE_UNIT;
		default:
			return FORMAT_STYLE_DECIMAL;
	}
};

export const formatConfigFromAggJiraNumberFieldFormatConfig = (
	gqlFormatConfig: AggJiraNumberFieldFormatConfigType | null | undefined,
): FormatConfigurationType => {
	const { formatDecimals, formatStyle, formatUnit } = gqlFormatConfig || {};
	return {
		formatStyle: formatStyleMapper(formatStyle),
		formatUnit: formatUnit || null,
		formatDecimals: formatDecimals ?? null,
	};
};
